import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import styled from 'styled-components'

import {
	CheckBoxFormField,
	Form,
	SelectFormField,
	Submit,
} from '@/components/UberForm'
import { useAppContext } from '@/hooks'
import type { CheckResult } from '@/rtkApi/mddApiSliceGenerated'
import { getApiErrorDescription, logError } from '@/utils/errorHandling'

import type { JsonImportParams } from '../../hooks/useImport'
import { useJsonCheckMessages } from './hooks/useJsonCheckMessages'

const INITIAL_VALUES: JsonImportParams = {
	existingElementsMode: 'OVERWRITE',
	createDomains: false,
	ignoreDomainDuplicities: false,
	ignoreInconsistentDomain: false,
	createStereotypes: false,
	ignoreMissingBo: false,
	ignoreMissingObjectTypes: false,
	ignoreTechnicalColumnCollision: false,
	detailReport: 'true',
}

const EXISTING_ELEMENTS_OPTIONS = [
	{ value: 'OVERWRITE', labelKey: 'IMPORT_EXISTING_ELEMENTS_OVERWRITE' },
	{ value: 'MERGE', labelKey: 'IMPORT_EXISTING_ELEMENTS_MERGE' },
]

const FK_OPTIONS = [
	{ value: 'IGNORE', labelKey: 'IMPORT_FK_IGNORE' },
	{ value: 'IMPORT', labelKey: 'IMPORT_FK_IMPORT' },
]

const FK_MORE_OPTIONS = [
	{ value: 'IGNORE', labelKey: 'IMPORT_FK_IGNORE' },
	{ value: 'IMPORT_EXITING', labelKey: 'IMPORT_FK_IMPORT_EXISTING' },
	{ value: 'IMPORT_IMPORTED', labelKey: 'IMPORT_FK_IMPORT_IMPORTED' },
]

type StructureImportJsonCheckProps = {
	checkLoading?: boolean
	checkResult: CheckResult | null | undefined
	onCheckResult: (result: CheckResult, params: JsonImportParams) => void
	onRunCheck: (params: JsonImportParams) => Promise<CheckResult>
}

export const StructureImportJsonCheck = ({
	checkResult,
	onRunCheck,
	onCheckResult,
	checkLoading = false,
}: StructureImportJsonCheckProps) => {
	const { t } = useAppContext()
	const getMessage = useJsonCheckMessages()
	const [checkError, setCheckError] = useState<string | null>(null)

	const onSubmit = async (values: JsonImportParams) => {
		setCheckError(null)
		try {
			const result = await onRunCheck(values)
			onCheckResult(result, values)
		} catch (error) {
			logError(error, { operation: 'StructureImportJsonCheck.onSubmit' })
			setCheckError(getApiErrorDescription(error))
		}
	}

	const hasErrors = (checkResult?.errors?.length ?? 0) > 0
	const hasWarnings = (checkResult?.warnings?.length ?? 0) > 0
	const checkPassed = checkResult && !hasErrors
	const emptyCell = t('NOT_AVAILABLE_SHORT')

	return (
		<div data-testid="structure-import-check">
			<StepTitle>{t('CHECK')}</StepTitle>
			<Form<JsonImportParams>
				onSubmit={onSubmit}
				initialValues={INITIAL_VALUES}
			>
				<ParamsGrid data-testid="structure-import-check-params">
					<SelectFormField
						name="existingElementsMode"
						title={t('IMPORT_EXISTING_ELEMENTS_MODE')}
						options={EXISTING_ELEMENTS_OPTIONS.map((o) => ({
							value: o.value,
							label: t(o.labelKey),
						}))}
						valueKey="value"
						labelKey="label"
						clearable={false}
					/>
					<CheckBoxFormField
						name="createDomains"
						title={t('IMPORT_CREATE_NEW_DOMAINS')}
					/>
					<CheckBoxFormField
						name="ignoreDomainDuplicities"
						title={t('IMPORT_IGNORE_DOMAIN_DUPLICITIES')}
					/>
					<CheckBoxFormField
						name="ignoreInconsistentDomain"
						title={t('IMPORT_IGNORE_INCONSISTENT_DOMAIN')}
					/>
					<CheckBoxFormField
						name="createStereotypes"
						title={t('IMPORT_CREATE_STEREOTYPES')}
					/>
					<CheckBoxFormField
						name="ignoreMissingBo"
						title={t('IMPORT_IGNORE_MISSING_BO')}
					/>
					<CheckBoxFormField
						name="ignoreMissingObjectTypes"
						title={t('IMPORT_IGNORE_MISSING_OBJECT_TYPES')}
					/>
					<CheckBoxFormField
						name="ignoreTechnicalColumnCollision"
						title={t('IMPORT_IGNORE_TECHNICAL_COLUMN_COLLISION')}
					/>
					<SelectFormField
						name="fkMissingTable"
						title={t('IMPORT_FK_MISSING_TABLE')}
						options={FK_OPTIONS.map((o) => ({
							value: o.value,
							label: t(o.labelKey),
						}))}
						valueKey="value"
						labelKey="label"
						clearable={true}
					/>
					<SelectFormField
						name="fkMoreTables"
						title={t('IMPORT_FK_MORE_TABLES')}
						options={FK_MORE_OPTIONS.map((o) => ({
							value: o.value,
							label: t(o.labelKey),
						}))}
						valueKey="value"
						labelKey="label"
						clearable={true}
					/>
				</ParamsGrid>
				<SubmitStyled disabled={checkLoading} data-testid="structure-import-button-check">
					{checkLoading ? t('CHECKING') : t('CHECK')}
				</SubmitStyled>
			</Form>

			{checkError && (
				<ApiErrorMessage data-testid="structure-import-check-api-error">
					{t('IMPORT_CHECK_FAILED')}: {checkError}
				</ApiErrorMessage>
			)}

			{checkPassed && (
				<SuccessMessage data-testid="structure-import-check-success">
					<FontAwesomeIcon icon={faCheckCircle} />
					{hasWarnings
						? t('IMPORT_STRUCTURE_READY_WITH_WARNINGS')
						: t('IMPORT_STRUCTURE_READY')}
				</SuccessMessage>
			)}

			{((checkResult?.errors?.length ?? 0) > 0 ||
				(checkResult?.warnings?.length ?? 0) > 0) && (
				<ResultsTableWrap data-testid="structure-import-check-results">
					<ResultsTable>
						<thead>
							<tr>
								<ResultsTh>{t('TYPE')}</ResultsTh>
								<ResultsTh>{t('MESSAGE')}</ResultsTh>
								<ResultsTh>{t('OBJECT')}</ResultsTh>
								<ResultsTh>{t('DETAIL')}</ResultsTh>
							</tr>
						</thead>
						<tbody>
							{checkResult?.errors?.map((msg, i) => (
								<ResultsTr
									key={`err-${i}-${msg.messageType}-${msg.object}`}
									$variant="error"
								>
									<ResultsTd>
										<ErrorBadge>{t('ERROR')}</ErrorBadge>
									</ResultsTd>
									<ResultsTd>{getMessage(msg.messageType)}</ResultsTd>
									<ResultsTd>{msg.object ?? emptyCell}</ResultsTd>
									<ResultsTd>{msg.detail ?? emptyCell}</ResultsTd>
								</ResultsTr>
							))}
							{checkResult?.warnings?.map((msg, i) => (
								<ResultsTr
									key={`warn-${i}-${msg.messageType}-${msg.object}`}
									$variant="warning"
								>
									<ResultsTd>
										<WarningBadge>{t('WARNING')}</WarningBadge>
									</ResultsTd>
									<ResultsTd>{getMessage(msg.messageType)}</ResultsTd>
									<ResultsTd>{msg.object ?? emptyCell}</ResultsTd>
									<ResultsTd>{msg.detail ?? emptyCell}</ResultsTd>
								</ResultsTr>
							))}
						</tbody>
					</ResultsTable>
				</ResultsTableWrap>
			)}
		</div>
	)
}

const StepTitle = styled.div`
	font-weight: 600;
	margin-bottom: 8px;
`

const ParamsGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px 16px;
	align-items: flex-end;
	margin-bottom: 12px;
`

const SubmitStyled = styled(Submit)`
	margin-bottom: 10px;
`

const SuccessMessage = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 12px 0;
	color: ${(props) => props.theme.colors?.success?.base ?? 'green'};
`

const ApiErrorMessage = styled.div`
	color: ${(props) => props.theme.colors.danger.base};
	margin: 12px 0;
	padding: 8px;
	background: ${(props) => props.theme.colors?.danger?.base ?? '#eb4034'}15;
	border-radius: 4px;
`

const ResultsTableWrap = styled.div`
	margin-top: 12px;
	overflow-x: auto;
	border: 1px solid
		${(props) => props.theme.colors?.layout?.borderColor ?? '#ddd'};
	border-radius: 4px;
`

const ResultsTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
`

const ResultsTh = styled.th`
	text-align: left;
	padding: 8px 10px;
	background: ${(props) =>
		props.theme.colors?.layout?.treePanel?.headerBackgroundColor ?? '#f3f3f3'};
	border-bottom: 1px solid
		${(props) => props.theme.colors?.layout?.borderColor ?? '#ddd'};
	font-weight: 600;
	white-space: nowrap;
`

const ResultsTr = styled.tr<{ $variant?: 'error' | 'warning' }>`
	${(props) =>
		props.$variant === 'error' &&
		`
		background: ${props.theme.colors?.danger?.base ?? '#eb4034'}12;
	`}
	${(props) =>
		props.$variant === 'warning' &&
		`
		background: ${props.theme.colors?.warn?.base ?? '#f0ad4e'}18;
	`}
	&:not(:last-child) td {
		border-bottom: 1px solid
			${(props) => props.theme.colors?.layout?.borderColor ?? '#eee'};
	}
`

const ResultsTd = styled.td`
	padding: 8px 10px;
	vertical-align: top;
	word-break: break-word;
	&:nth-child(1) {
		width: 1%;
		white-space: nowrap;
	}
	&:nth-child(2) {
		min-width: 140px;
	}
	&:nth-child(3),
	&:nth-child(4) {
		min-width: 120px;
		max-width: 240px;
	}
`

const ErrorBadge = styled.span`
	display: inline-block;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
	background: ${(props) => props.theme.colors.danger.base};
	color: #fff;
`

const WarningBadge = styled.span`
	display: inline-block;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
	background: ${(props) => props.theme.colors.warn.base};
	color: #fff;
`
